import {
  Captions,
  Maximize,
  Pause,
  Play,
  RotateCcw,
  Volume2,
  VolumeX,
} from "lucide-react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useTranslation } from "react-i18next";
import {
  fetchCourseTranscript,
  findActiveTranscriptSegmentIndex,
  getTranscriptCaptionWindow,
  type CourseTranscript,
} from "@/lib/courses/courseTranscript";
import type { CourseChapter } from "@/types/course";

type CourseVideoPlayerProps = {
  src: string;
  title: string;
  poster?: string | null;
  chapters?: CourseChapter[];
  transcriptUrl?: string | null;
  onTimeChange?: (seconds: number) => void;
  onEnded?: () => void;
};

export type CourseVideoPlayerHandle = {
  seekTo: (seconds: number) => void;
};

const CONTROLS_HIDE_DELAY_MS = 2400;
const PLAYBACK_RATES = [1, 1.25, 1.5, 1.75, 2, 3] as const;

function formatTime(value: number): string {
  if (!Number.isFinite(value) || value < 0) return "0:00";

  const totalSeconds = Math.floor(value);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  }

  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

export const CourseVideoPlayer = forwardRef<
  CourseVideoPlayerHandle,
  CourseVideoPlayerProps
>(function CourseVideoPlayer(
  {
    src,
    title,
    poster,
    chapters = [],
    transcriptUrl,
    onTimeChange,
    onEnded,
  },
  ref
) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hideControlsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSeekRef = useRef<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [hasError, setHasError] = useState(false);
  const [activeSrc, setActiveSrc] = useState<string | null>(null);
  const [subtitlesEnabled, setSubtitlesEnabled] = useState(false);
  const [loadedSubtitleTranscript, setLoadedSubtitleTranscript] = useState<{
    url: string;
    transcript: CourseTranscript;
  } | null>(null);
  const [subtitleLoadingUrl, setSubtitleLoadingUrl] = useState<string | null>(
    null
  );
  const subtitleTranscript =
    loadedSubtitleTranscript &&
    loadedSubtitleTranscript.url === transcriptUrl
      ? loadedSubtitleTranscript.transcript
      : null;
  const isSubtitleLoading =
    subtitlesEnabled && subtitleLoadingUrl === transcriptUrl;

  const clearHideControlsTimer = useCallback(() => {
    if (hideControlsTimerRef.current) {
      clearTimeout(hideControlsTimerRef.current);
      hideControlsTimerRef.current = null;
    }
  }, []);

  const scheduleControlsHide = useCallback(() => {
    clearHideControlsTimer();
    if (!videoRef.current?.paused) {
      hideControlsTimerRef.current = setTimeout(() => {
        setShowControls(false);
      }, CONTROLS_HIDE_DELAY_MS);
    }
  }, [clearHideControlsTimer]);

  useEffect(() => clearHideControlsTimer, [clearHideControlsTimer]);

  useEffect(() => {
    const video = videoRef.current;
    pendingSeekRef.current = null;
    setActiveSrc(null);
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
    setIsBuffering(false);
    setHasError(false);
    setShowControls(true);
    onTimeChange?.(0);

    return () => {
      if (!video) return;
      video.pause();
      video.removeAttribute("src");
      video.load();
    };
  }, [onTimeChange, src]);

  useEffect(() => {
    if (
      !subtitlesEnabled ||
      !transcriptUrl ||
      loadedSubtitleTranscript?.url === transcriptUrl
    ) {
      return;
    }

    const controller = new AbortController();
    let isActiveRequest = true;
    setSubtitleLoadingUrl(transcriptUrl);

    fetchCourseTranscript(transcriptUrl, controller.signal)
      .then((transcript) => {
        if (!isActiveRequest) return;
        setLoadedSubtitleTranscript({ url: transcriptUrl, transcript });
        setSubtitleLoadingUrl((loadingUrl) =>
          loadingUrl === transcriptUrl ? null : loadingUrl
        );
      })
      .catch((error: unknown) => {
        if (!isActiveRequest) return;
        setSubtitleLoadingUrl((loadingUrl) =>
          loadingUrl === transcriptUrl ? null : loadingUrl
        );
        if (error instanceof DOMException && error.name === "AbortError") return;
        setSubtitlesEnabled(false);
      });

    return () => {
      isActiveRequest = false;
      controller.abort();
    };
  }, [loadedSubtitleTranscript?.url, subtitlesEnabled, transcriptUrl]);

  useEffect(() => {
    if (activeSrc !== src) return;

    const video = videoRef.current;
    if (!video) return;

    video.load();
    void video.play().catch(() => {
      setHasError(true);
      setIsBuffering(false);
    });
  }, [activeSrc, src]);

  const seekTo = useCallback(
    (seconds: number) => {
      const nextTime = Math.max(0, seconds);
      const video = videoRef.current;
      if (!video) return;

      setShowControls(true);
      setCurrentTime(nextTime);
      onTimeChange?.(nextTime);

      if (activeSrc !== src) {
        pendingSeekRef.current = nextTime;
        setIsBuffering(true);
        setActiveSrc(src);
        return;
      }

      video.currentTime = nextTime;
      void video.play().catch(() => {
        setHasError(true);
        setIsBuffering(false);
      });
    },
    [activeSrc, onTimeChange, src]
  );

  useImperativeHandle(ref, () => ({ seekTo }), [seekTo]);

  const togglePlayback = useCallback(async () => {
    const video = videoRef.current;
    if (!video || hasError) return;

    if (activeSrc !== src) {
      setIsBuffering(true);
      setActiveSrc(src);
      return;
    }

    if (video.paused) {
      try {
        await video.play();
      } catch {
        setHasError(true);
      }
    } else {
      video.pause();
    }
  }, [activeSrc, hasError, src]);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    if (!containerRef.current) return;

    if (document.fullscreenElement) {
      await document.exitFullscreen?.();
    } else {
      await containerRef.current.requestFullscreen?.();
    }
  }, []);

  const handleKeyboard = (event: KeyboardEvent<HTMLDivElement>) => {
    const video = videoRef.current;
    if (!video) return;
    if (event.target !== event.currentTarget) return;

    if (event.key === " " || event.key.toLowerCase() === "k") {
      event.preventDefault();
      void togglePlayback();
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      video.currentTime = Math.max(0, video.currentTime - 5);
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      video.currentTime = Math.min(duration, video.currentTime + 5);
    } else if (event.key.toLowerCase() === "m") {
      event.preventDefault();
      toggleMute();
    } else if (event.key.toLowerCase() === "f") {
      event.preventDefault();
      void toggleFullscreen();
    }
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const activeChapter = chapters.reduce<CourseChapter | null>(
    (current, chapter) =>
      chapter.startSeconds <= currentTime ? chapter : current,
    null
  );
  const controlsVisible = showControls || !isPlaying || hasError;
  const activeSubtitleSegmentIndex = subtitleTranscript
    ? findActiveTranscriptSegmentIndex(
        subtitleTranscript.segments,
        currentTime
      )
    : -1;
  const subtitleCaption =
    activeSubtitleSegmentIndex >= 0 && subtitleTranscript
      ? getTranscriptCaptionWindow(
          subtitleTranscript.segments[activeSubtitleSegmentIndex],
          currentTime
        )
      : null;

  return (
    <div
      ref={containerRef}
      role="group"
      aria-label={t("course.videoPlayer.label", { title })}
      tabIndex={0}
      onKeyDown={handleKeyboard}
      onPointerMove={() => {
        setShowControls(true);
        scheduleControlsHide();
      }}
      onPointerLeave={() => {
        if (isPlaying) setShowControls(false);
      }}
      className="group relative aspect-video w-full overflow-hidden rounded-2xl border border-primary/20 bg-black shadow-[0_24px_80px_-28px_hsl(var(--primary)/0.38)] outline-none ring-offset-background transition-shadow focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
    >
      <video
        ref={videoRef}
        src={activeSrc === src ? src : undefined}
        title={title}
        poster={poster ?? undefined}
        preload="none"
        playsInline
        className="h-full w-full bg-black object-contain"
        onClick={() => void togglePlayback()}
        onLoadedMetadata={(event) => {
          event.currentTarget.playbackRate = playbackRate;
          setDuration(event.currentTarget.duration);
          setHasError(false);
          if (pendingSeekRef.current !== null) {
            const nextTime = Math.min(
              pendingSeekRef.current,
              event.currentTarget.duration
            );
            pendingSeekRef.current = null;
            event.currentTarget.currentTime = nextTime;
            setCurrentTime(nextTime);
          }
        }}
        onTimeUpdate={(event) => {
          const nextTime = event.currentTarget.currentTime;
          setCurrentTime(nextTime);
          onTimeChange?.(nextTime);
        }}
        onPlay={() => {
          setIsPlaying(true);
          setIsBuffering(false);
          scheduleControlsHide();
        }}
        onPause={() => {
          setIsPlaying(false);
          setShowControls(true);
          clearHideControlsTimer();
        }}
        onEnded={onEnded}
        onWaiting={() => setIsBuffering(true)}
        onCanPlay={() => setIsBuffering(false)}
        onError={() => {
          setHasError(true);
          setIsBuffering(false);
          setIsPlaying(false);
        }}
      />

      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_85%_10%,hsl(var(--brand-secondary)/0.12),transparent_34%)]" />

      {subtitlesEnabled && subtitleCaption?.words.length ? (
        <div
          className={`pointer-events-none absolute inset-x-4 z-20 flex justify-center transition-[bottom] duration-300 ${
            controlsVisible ? "bottom-[5.75rem]" : "bottom-6"
          }`}
        >
          <p
            data-testid="course-subtitle-overlay"
            className="max-w-3xl rounded-xl border border-white/10 bg-black/75 px-3.5 py-2 text-center text-[clamp(0.9rem,2.2vw,1.25rem)] font-semibold leading-relaxed text-white shadow-xl backdrop-blur-md"
          >
            {subtitleCaption.words.map((word, index) => {
              const isActiveWord =
                index === subtitleCaption.activeWordIndex;
              return (
                <span
                  key={`${word.start}-${index}`}
                  data-active-word={isActiveWord ? "true" : undefined}
                  className={
                    isActiveWord
                      ? "rounded bg-primary px-0.5 text-primary-foreground shadow-[0_0_14px_hsl(var(--primary)/0.45)]"
                      : index < subtitleCaption.activeWordIndex
                        ? "text-white"
                        : "text-white/65"
                  }
                >
                  {word.text}
                  {index < subtitleCaption.words.length - 1 ? " " : ""}
                </span>
              );
            })}
          </p>
        </div>
      ) : null}

      {!hasError && (!isPlaying || isBuffering) && (
        <button
          type="button"
          onClick={() => void togglePlayback()}
          aria-label={
            isPlaying
              ? t("course.videoPlayer.pause")
              : t("course.videoPlayer.play")
          }
          className="absolute left-1/2 top-1/2 grid h-16 w-16 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-white/20 bg-black/55 text-white shadow-[0_12px_45px_hsl(var(--primary)/0.2)] backdrop-blur-md transition duration-200 hover:scale-105 hover:border-primary/60 hover:bg-primary hover:text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-black"
        >
          {isBuffering ? (
            <span className="h-6 w-6 animate-spin rounded-full border-2 border-white/30 border-t-primary" />
          ) : (
            <Play className="ml-1 h-7 w-7 fill-current" />
          )}
        </button>
      )}

      {hasError && (
        <div className="absolute inset-0 grid place-items-center bg-black/80 px-6 text-center backdrop-blur-sm">
          <div className="max-w-sm space-y-3">
            <div className="mx-auto grid h-12 w-12 place-items-center rounded-full border border-primary/25 bg-primary/10 text-primary">
              <RotateCcw className="h-5 w-5" />
            </div>
            <p className="text-sm font-semibold text-white">
              {t("course.videoPlayer.errorTitle")}
            </p>
            <p className="text-xs leading-relaxed text-white/55">
              {t("course.videoPlayer.errorBody")}
            </p>
            <button
              type="button"
              onClick={() => {
                const video = videoRef.current;
                if (!video) return;
                setHasError(false);
                video.load();
              }}
              className="rounded-full border border-primary/35 bg-primary/10 px-4 py-2 text-xs font-semibold text-primary transition hover:bg-primary hover:text-primary-foreground"
            >
              {t("course.videoPlayer.retry")}
            </button>
          </div>
        </div>
      )}

      {!hasError && (
        <div
          className={`absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/85 to-transparent px-3 pb-3 pt-14 transition-all duration-300 sm:px-4 ${
            controlsVisible
              ? "translate-y-0 opacity-100"
              : "pointer-events-none translate-y-3 opacity-0"
          }`}
        >
          <div className="relative mb-3 flex h-3 items-center">
            <input
              type="range"
              min={0}
              max={duration || 0}
              step={0.1}
              value={Math.min(currentTime, duration || 0)}
              aria-label={t("course.videoPlayer.seek")}
              onChange={(event) => {
                const nextTime = Number(event.target.value);
                if (videoRef.current) videoRef.current.currentTime = nextTime;
                setCurrentTime(nextTime);
              }}
              className="absolute inset-x-0 z-10 h-1.5 w-full cursor-pointer appearance-none rounded-full accent-primary"
              style={{
                background: `linear-gradient(to right, hsl(var(--primary)) ${progress}%, rgb(255 255 255 / 0.22) ${progress}%)`,
              }}
            />
            {duration > 0 &&
              chapters.slice(1).map((chapter) => (
                <span
                  key={`${chapter.startSeconds}-${chapter.title}`}
                  data-testid="course-chapter-marker"
                  aria-hidden="true"
                  className="pointer-events-none absolute top-1/2 z-20 h-2.5 w-[3px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-black/90 shadow-[0_0_0_1px_rgb(255_255_255/0.18)]"
                  style={{
                    left: `${Math.min(
                      100,
                      Math.max(0, (chapter.startSeconds / duration) * 100)
                    )}%`,
                  }}
                />
              ))}
          </div>

          <div className="flex items-center gap-1.5 text-white sm:gap-2">
            <button
              type="button"
              onClick={() => void togglePlayback()}
              aria-label={
                isPlaying
                  ? t("course.videoPlayer.pause")
                  : t("course.videoPlayer.play")
              }
              className="grid h-9 w-9 place-items-center rounded-full transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {isPlaying ? (
                <Pause className="h-4 w-4 fill-current" />
              ) : (
                <Play className="ml-0.5 h-4 w-4 fill-current" />
              )}
            </button>

            <span className="min-w-[76px] text-[11px] font-medium tabular-nums text-white/75 sm:text-xs">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>

            {activeChapter && (
              <span
                className="hidden min-w-0 truncate text-xs font-medium text-white/65 md:block"
                title={activeChapter.title}
              >
                {activeChapter.title}
              </span>
            )}

            <div className="ml-auto flex items-center gap-1">
              <select
                value={playbackRate}
                aria-label={t("course.videoPlayer.playbackRate")}
                onChange={(event) => {
                  const nextRate = Number(event.target.value);
                  if (videoRef.current) {
                    videoRef.current.playbackRate = nextRate;
                  }
                  setPlaybackRate(nextRate);
                }}
                className="h-8 w-[58px] cursor-pointer rounded-md border border-white/15 bg-black/35 px-1 text-[11px] font-semibold tabular-nums text-white outline-none transition hover:bg-white/10 focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary sm:w-16 sm:px-1.5 sm:text-xs"
              >
                {PLAYBACK_RATES.map((rate) => (
                  <option key={rate} value={rate} className="bg-black text-white">
                    {rate}x
                  </option>
                ))}
              </select>

              <button
                type="button"
                onClick={toggleMute}
                aria-label={
                  isMuted || volume === 0
                    ? t("course.videoPlayer.unmute")
                    : t("course.videoPlayer.mute")
                }
                className="grid h-9 w-9 place-items-center rounded-full transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {isMuted || volume === 0 ? (
                  <VolumeX className="h-4 w-4" />
                ) : (
                  <Volume2 className="h-4 w-4" />
                )}
              </button>

              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={isMuted ? 0 : volume}
                aria-label={t("course.videoPlayer.volume")}
                onChange={(event) => {
                  const nextVolume = Number(event.target.value);
                  const video = videoRef.current;
                  if (!video) return;
                  video.volume = nextVolume;
                  video.muted = nextVolume === 0;
                  setVolume(nextVolume);
                  setIsMuted(nextVolume === 0);
                }}
                className="hidden h-1 w-16 cursor-pointer appearance-none rounded-full bg-white/20 accent-primary sm:block"
              />

              {transcriptUrl && (
                <button
                  type="button"
                  onClick={() => setSubtitlesEnabled((enabled) => !enabled)}
                  aria-pressed={subtitlesEnabled}
                  aria-label={
                    subtitlesEnabled
                      ? t("course.videoPlayer.subtitlesOff")
                      : t("course.videoPlayer.subtitlesOn")
                  }
                  className={`grid h-9 w-9 place-items-center rounded-full transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                    subtitlesEnabled
                      ? "bg-primary text-primary-foreground hover:bg-primary/90"
                      : "hover:bg-white/10"
                  }`}
                >
                  {isSubtitleLoading ? (
                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  ) : (
                    <Captions className="h-[18px] w-[18px]" />
                  )}
                </button>
              )}

              <button
                type="button"
                onClick={() => void toggleFullscreen()}
                aria-label={t("course.videoPlayer.fullscreen")}
                className="grid h-9 w-9 place-items-center rounded-full transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <Maximize className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});
